<template>
  <div class="dod-container" v-if="!loading">
    <dod-view-title :title="'Zlecenia'" />
    <dod-search-input
      :withMonthSwitcher="true"
      :with-filters="true"
      :filter-settings="[
        'CUSTOMER_STATUS',
        'DEPARTMENT',
        'PRIORITY_STATUS',
        'SCOPE_OF_CARE',
      ]"
      @search="handleSearch"
      @onFilter="handleFilters"
      @onMonthChange="onMonthChange"
      :date="date"
    />
    <pagination-arrows
      v-if="orders.meta && orders.meta.total"
      :onPage="orders.meta.per_page"
      :pageNumber="orders.meta.current_page"
      :total="orders.meta.total"
      @nextPage="handleNextPage"
      @previousPage="handlePreviousPage"
    />
    <div v-if="selectedCount" class="selected-info">
      Zaznaczono zleceń: {{ selectedCount }}
      <br />
      <span class="clear-selection" @click="clearSelection">
        Wyczyść zaznaczenie
      </span>
    </div>

    <table
      class="table table-striped"
      v-if="orders && orders.data && orders.data.length > 0"
    >
      <thead>
        <tr>
          <td></td>
          <td>ID</td>
          <td>Podopieczny</td>
          <td>Opiekun</td>
          <td>Status</td>
          <td>Wydział</td>
          <td>Adres</td>
          <td>Numer telefonu</td>
          <td>Dni</td>
          <td>Ilość godz. zaplanowanych</td>
          <td>Ilość godz. wykonanych</td>
          <td>
            <img
              v-if="orders.data && orders.data.length > 0"
              class="printer-icon"
              @click="generatePdf"
              src="../../icons/dod/printer.svg"
              alt=""
              title="Drukuj zaznaczone"
            />
            <span class="print-all">
              <img
                v-if="orders.data && orders.data.length > 0"
                class="printer-icon ml-4"
                @click="generatePdfForAll"
                src="../../icons/dod/printer.svg"
                alt=""
                title="Drukuj wszystkie"
              />
              <span class="total-counter">{{ orders.meta.total }}</span>
            </span>
          </td>
        </tr>
      </thead>
      <tbody>
        <tr v-for="order in orders.data" :key="order.id">
          <td>
            <div class="td-icons">
              <input
                type="checkbox"
                :checked="!!selectedOrders[order.id]"
                @change="toggleOrder(order)"
              />
            </div>
          </td>
          <td>{{ order.id }}</td>
          <td>{{ order.customer.firstName }} {{ order.customer.lastName }}</td>
          <td>
            {{ order.employee.firstName }} {{ order.employee.lastName }}
            <span
              v-if="
                order.employee.mainAppUserId && panelCan('showMainAppLinking')
              "
              class="seniorapp-label"
              >{{ $mainAppBadge }}</span
            >
          </td>
          <td>{{ order.status.name }}</td>
          <td>{{ order.department.name }}</td>
          <td>{{ order.customer.address }}</td>
          <td>{{ order.customer.phone }}</td>
          <td>{{ order.days }}</td>
          <td>{{ getNumberOfScheduledHoursFromOrder(order) }}</td>
          <td>{{ getNumberOfMadeHoursFromOrder(order) }}</td>
          <td>
            <gradient-button
              @handleClick="moveToCustomerDetails(order.customer.id)"
              :title="'Przejdź'"
              :customStyles="'width: 5vw; font-size: calc(11px + 0.05vw);'"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <dod-empty-list v-else />
  </div>
</template>

<script>
import DodSearchInput from "../../components/dod/DodSearchInput.vue";
import DodViewTitle from "../../components/dod/DodViewTitle.vue";
import moment from "moment";
import GradientButton from "../../components/dod/GradientButton.vue";
import DodEmptyList from "../../components/dod/DodEmptyList.vue";
import shared from "../../shared";
import PaginationArrows from "../../components/PaginationArrows.vue";
const defaultLimit = 20;
const allLimit = Number.MAX_SAFE_INTEGER;

export default {
  components: {
    DodViewTitle,
    DodSearchInput,
    GradientButton,
    DodEmptyList,
    PaginationArrows,
  },
  name: "DODOrdersList",

  data() {
    return {
      loading: true,
      month: "",
      year: "",
      filters: {},
      search: "",
      date: "",
      selectedOrders: {},
      limit: defaultLimit,
      page: 1,
    };
  },

  async mounted() {
    const now = moment();
    this.date = now.toDate();
    this.month = now.get("month") + 1;
    this.year = now.get("year");
    await this.fetchOrders();
    this.loading = false;
  },

  methods: {
    async handleNextPage() {
      this.page++;
      await this.fetchOrders();
    },
    async handlePreviousPage() {
      this.page--;
      await this.fetchOrders();
    },
    toggleOrder(order) {
      if (this.selectedOrders[order.id]) {
        this.$delete(this.selectedOrders, order.id);
      } else {
        this.$set(this.selectedOrders, order.id, order);
      }
    },
    clearSelection() {
      this.selectedOrders = {};
    },
    // Druk zaznaczonych - z mapy pełnych obiektów, więc działa niezależnie od bieżącej strony.
    async generatePdf() {
      for (const order of Object.values(this.selectedOrders)) {
        await this.handlePrint(order);
      }
    },
    async generatePdfForAll() {
      // Chwilowo dociągam wszystkie zlecenia miesiąca (bez paginacji) tylko po to, by wydrukować,
      // po czym wracam do bieżącej strony.
      await this.fetchOrders(1, allLimit);
      const orders = [...this.orders.data];
      for (const order of orders) {
        await this.handlePrint(order);
      }
      await this.fetchOrders();
    },
    async handlePrint(order) {
      const response = await this.$store.dispatch("generateWorkingCardPdf", {
        orderId: order.id,
        month: this.month,
        year: this.year,
      });
      const fileURL = window.URL.createObjectURL(new Blob([response]));
      const fileLink = document.createElement("a");
      const monthName = shared.getPolishMonthNameByMonthNumber(this.month - 1);
      const employee = order.employee;
      const customer = order.customer;
      const filename = `Karta pracy opiekuna ${employee.firstName} ${
        employee.lastName
      } i podopiecznego ${customer.firstName} ${
        customer.lastName
      } na miesiąc ${monthName.toLowerCase()}.pdf`;

      fileLink.href = fileURL;
      fileLink.setAttribute("download", filename);
      document.body.appendChild(fileLink);
      fileLink.click();
    },

    async fetchOrders(page, limit) {
      await this.$store.dispatch("getDodOrdersInMonth", {
        search: this.search,
        filters: this.filters,
        month: this.month,
        year: this.year,
        limit: limit ? limit : this.limit,
        page: page ? page : this.page,
      });
    },

    async onMonthChange(date) {
      const dateToSet = moment(date);
      this.date = dateToSet.toDate();
      this.month = dateToSet.get("month") + 1;
      this.year = dateToSet.get("year");
      this.page = 1;
      this.clearSelection();
      await this.fetchOrders();
    },
    async handleSearch(searchValue) {
      this.search = searchValue;
      this.page = 1;
      this.clearSelection();
      await this.fetchOrders();
    },

    async handleFilters(filters) {
      this.filters = {};
      if (filters) {
        filters.forEach((filter) => (this.filters[filter.key] = filter.value));
      }
      this.page = 1;
      this.clearSelection();
      await this.fetchOrders();
    },

    getNumberOfScheduledHoursFromOrder(order) {
      return order.daysDates.reduce(
        (hours, day) => (hours = hours + day.numberOfHours),
        0
      );
    },

    getNumberOfMadeHoursFromOrder(order) {
      return order.daysDates.reduce(
        (hours, day) => (hours = hours + day.numberOfMadeHours),
        0
      );
    },

    moveToCustomerDetails(customerId) {
      this.$router.push({
        name: "DodCustomerDetails",
        params: { id: customerId },
      });
    },
  },
  computed: {
    orders() {
      return this.$store.getters.getDodOrdersInMonth;
    },
    selectedCount() {
      return Object.keys(this.selectedOrders).length;
    },
  },
};
</script>

<style scoped>
table {
  table-layout: fixed;
  font-size: calc(11px + 0.05vw);
}
.printer-icon {
  cursor: pointer;
}
.print-all {
  position: relative;
}
.total-counter {
  position: absolute;
  top: 18px;
  right: -10px;
  font-size: calc(11px + 0.05vw);
}
.selected-info {
  margin: 8px 0;
  font-size: calc(11px + 0.05vw);
  font-weight: 600;
}
.clear-selection {
  font-weight: 400;
  text-decoration: underline;
  cursor: pointer;
}
</style>